import { FormControl } from '@angular/forms'
import { PaginatorInfoFragment } from '@app-graphql/schema'
import { Localization } from '@aa-app-localization'
import { TranslationKey } from '@app-pipes/typed-translate/typed-translate.pipe'

/**
 * Union type of `null` and `undefined`. Works nicely in conjunction with Ramda's `isNil` function.
 */
export type Nil = null | undefined

// ------------------------------------------------------------------------------
//      Async / Await type encoding
// ------------------------------------------------------------------------------

/**
 * Wraps the given type in a Promise. If the given type is itself a promise, the type
 * is returned as-is.
 *
 * @example
 * type T = Async<number>          // T :: Promise<number>
 * type U = Async<Promise<string>> // U :: Promise<string>
 */
export type Async<T> = T extends Promise<any> ? T : Promise<T>

/**
 * Flattens promise types into their resolved types. If a non-promise type is given, the type
 * is returned as-is.
 *
 * @example
 * type T = Await<Promise<number>> // T :: number
 * type U = Await<string>          // U :: string
 */
export type Await<T> = T extends Promise<infer U> ? U : T

export type Unpacked<T, ReadOnly = false> = ReadOnly extends true
    ? T extends ReadonlyArray<infer U> ? U : T
    : T extends (infer U)[] ? U : T


// ------------------------------------------------------------------------------
//      Function type wrappers
// ------------------------------------------------------------------------------

export type UnaryFunction<T, U> = (x: T) => U
export type BinaryFunction<T, U, V> = (x: T, y: U) => V

// ------------------------------------------------------------------------------
//      Convert tuples to unions
// ------------------------------------------------------------------------------

export type TupleToUnion<T, TAcc = never> = T extends readonly [infer THead, ...infer TTail]
    ? TupleToUnion<TTail, TAcc | THead>
    : TAcc

export type TupleOverloads<T = any> =
    | readonly [T]
    | readonly [T, T]
    | readonly [T, T, T]
    | readonly [T, T, T, T]
    | readonly [T, T, T, T, T]
    | readonly [T, T, T, T, T, T]
    | readonly [T, T, T, T, T, T, T]
    | readonly [T, T, T, T, T, T, T, T]
    | readonly [T, T, T, T, T, T, T, T, T]
    | readonly [T, T, T, T, T, T, T, T, T, T]
    | readonly [T, T, T, T, T, T, T, T, T, T, T]
    | readonly [T, T, T, T, T, T, T, T, T, T, T, T]

export type GraphQLInputForm<
    T extends { [k: string]: any },
    Nullable extends Boolean = true,
> = Nullable extends true ? {
    [k in keyof T]: FormControl<T[k] | null>
} : {
    [k in keyof T]: FormControl<T[k]>
}

export type Translatable = {
    translations: {
        [k in Localization.Locale]: any
    }
}

export type Translated<T extends Translatable> = T & T['translations'][Localization.Locale]

export type PaginatedData<T> = {
    paginatorInfo: PaginatorInfoFragment
    data: T[]
}

export type PageStatus = 'ready' | 'loading' | 'not-found'

interface IPageState<T> {
    status: PageStatus
    data: T
}

export type PageState<D, Status = PageStatus> = Status extends 'ready'
    ? IPageState<D>
    : Pick<IPageState<D>, 'status'>

export enum CountriesEnum {
    ad = 'ad',
    ae = 'ae',
    af = 'af',
    ag = 'ag',
    ai = 'ai',
    al = 'al',
    am = 'am',
    ao = 'ao',
    aq = 'aq',
    ar = 'ar',
    as = 'as',
    at = 'at',
    au = 'au',
    aw = 'aw',
    ax = 'ax',
    az = 'az',
    ba = 'ba',
    bb = 'bb',
    bd = 'bd',
    be = 'be',
    bf = 'bf',
    bg = 'bg',
    bh = 'bh',
    bi = 'bi',
    bj = 'bj',
    bl = 'bl',
    bm = 'bm',
    bn = 'bn',
    bo = 'bo',
    bq = 'bq',
    br = 'br',
    bs = 'bs',
    bt = 'bt',
    bv = 'bv',
    bw = 'bw',
    by = 'by',
    bz = 'bz',
    ca = 'ca',
    cc = 'cc',
    cd = 'cd',
    cf = 'cf',
    cg = 'cg',
    ch = 'ch',
    ci = 'ci',
    ck = 'ck',
    cl = 'cl',
    cm = 'cm',
    cn = 'cn',
    co = 'co',
    cr = 'cr',
    cu = 'cu',
    cv = 'cv',
    cw = 'cw',
    cx = 'cx',
    cy = 'cy',
    cz = 'cz',
    de = 'de',
    dj = 'dj',
    dk = 'dk',
    dm = 'dm',
    do = 'do',
    dz = 'dz',
    ec = 'ec',
    ee = 'ee',
    eg = 'eg',
    eh = 'eh',
    en = 'en',
    er = 'er',
    es = 'es',
    et = 'et',
    fi = 'fi',
    fj = 'fj',
    fk = 'fk',
    fm = 'fm',
    fo = 'fo',
    fr = 'fr',
    ga = 'ga',
    gb = 'gb',
    gd = 'gd',
    ge = 'ge',
    gf = 'gf',
    gg = 'gg',
    gh = 'gh',
    gi = 'gi',
    gl = 'gl',
    gm = 'gm',
    gn = 'gn',
    gp = 'gp',
    gq = 'gq',
    gr = 'gr',
    gs = 'gs',
    gt = 'gt',
    gu = 'gu',
    gw = 'gw',
    gy = 'gy',
    hk = 'hk',
    hm = 'hm',
    hn = 'hn',
    hr = 'hr',
    ht = 'ht',
    hu = 'hu',
    id = 'id',
    ie = 'ie',
    il = 'il',
    im = 'im',
    in = 'in',
    io = 'io',
    iq = 'iq',
    ir = 'ir',
    is = 'is',
    it = 'it',
    je = 'je',
    jm = 'jm',
    jo = 'jo',
    jp = 'jp',
    ke = 'ke',
    kg = 'kg',
    kh = 'kh',
    ki = 'ki',
    km = 'km',
    kn = 'kn',
    kp = 'kp',
    kr = 'kr',
    kw = 'kw',
    ky = 'ky',
    kz = 'kz',
    la = 'la',
    lb = 'lb',
    lc = 'lc',
    li = 'li',
    lk = 'lk',
    lr = 'lr',
    ls = 'ls',
    lt = 'lt',
    lu = 'lu',
    lv = 'lv',
    ly = 'ly',
    ma = 'ma',
    mc = 'mc',
    md = 'md',
    me = 'me',
    mf = 'mf',
    mg = 'mg',
    mh = 'mh',
    mk = 'mk',
    ml = 'ml',
    mm = 'mm',
    mn = 'mn',
    mo = 'mo',
    mp = 'mp',
    mq = 'mq',
    mr = 'mr',
    ms = 'ms',
    mt = 'mt',
    mu = 'mu',
    mv = 'mv',
    mw = 'mw',
    mx = 'mx',
    my = 'my',
    mz = 'mz',
    na = 'na',
    nc = 'nc',
    ne = 'ne',
    nf = 'nf',
    ng = 'ng',
    ni = 'ni',
    nl = 'nl',
    no = 'no',
    np = 'np',
    nr = 'nr',
    nu = 'nu',
    nz = 'nz',
    om = 'om',
    pa = 'pa',
    pe = 'pe',
    pf = 'pf',
    pg = 'pg',
    ph = 'ph',
    pk = 'pk',
    pl = 'pl',
    pm = 'pm',
    pn = 'pn',
    pr = 'pr',
    ps = 'ps',
    pt = 'pt',
    pw = 'pw',
    py = 'py',
    qa = 'qa',
    re = 're',
    ro = 'ro',
    rs = 'rs',
    ru = 'ru',
    rw = 'rw',
    sa = 'sa',
    sb = 'sb',
    sc = 'sc',
    sd = 'sd',
    se = 'se',
    sg = 'sg',
    sh = 'sh',
    si = 'si',
    sj = 'sj',
    sk = 'sk',
    sl = 'sl',
    sm = 'sm',
    sn = 'sn',
    so = 'so',
    sr = 'sr',
    ss = 'ss',
    st = 'st',
    sv = 'sv',
    sx = 'sx',
    sy = 'sy',
    sz = 'sz',
    tc = 'tc',
    td = 'td',
    tf = 'tf',
    tg = 'tg',
    th = 'th',
    tj = 'tj',
    tk = 'tk',
    tl = 'tl',
    tm = 'tm',
    tn = 'tn',
    to = 'to',
    tr = 'tr',
    tt = 'tt',
    tv = 'tv',
    tw = 'tw',
    tz = 'tz',
    ua = 'ua',
    ug = 'ug',
    um = 'um',
    us = 'us',
    uy = 'uy',
    uz = 'uz',
    va = 'va',
    vc = 'vc',
    ve = 've',
    vg = 'vg',
    vi = 'vi',
    vn = 'vn',
    vu = 'vu',
    wf = 'wf',
    ws = 'ws',
    ye = 'ye',
    yt = 'yt',
    za = 'za',
    zm = 'zm',
    zw = 'zw',
}

export enum AddressType {
    Delivery = 'delivery',
    Post = 'post',
    Visit = 'visit',
}

export interface TranslationLabel {
    label: TranslationKey
    value: string
}
